import {
    Box,
    Tabs,
    TabList,
    Tab,
    useColorModeValue,
    Flex,
    Text,
    keyframes,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { FaGraduationCap, FaBriefcase } from "react-icons/fa";
import Education from "./Education";
import Experience from "./Experience";

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const EducationExperience = () => {
    const sectionBg = useColorModeValue("gray.50", "gray.900");
    const headerColor = useColorModeValue("gray.800", "white");
    const tabBg = useColorModeValue("white", "gray.800");
    const tabSelectedBg = useColorModeValue("blue.500", "blue.500");
    const tabColor = useColorModeValue("gray.500", "gray.400");
    const tabSelectedColor = "white";
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const [tabIndex, setTabIndex] = useState(0);

    return (
        <Box
            id="education-experience"
            width="100%"
            py={16}
            bg={sectionBg}
            position="relative"
            overflow="hidden"
        >
            {/* Background Decorative Elements */}
            <Box
                position="absolute"
                top="10%"
                left="-10%"
                width="300px"
                height="300px"
                bg="blue.100"
                borderRadius="full"
                opacity={0.3}
                filter="blur(60px)"
                pointerEvents="none"
            />
            <Box
                position="absolute"
                bottom="10%"
                right="-10%"
                width="400px"
                height="400px"
                bg="purple.100"
                borderRadius="full"
                opacity={0.2}
                filter="blur(80px)"
                pointerEvents="none"
            />

            <Box maxWidth="1200px" mx="auto" px={{ base: 4, md: 8 }} position="relative" zIndex={1}>
                {/* Section Header */}
                <Flex
                    direction="column"
                    align="center"
                    mb={12}
                    animation={`${slideIn} 0.6s ease-out`}
                >
                    <Text
                        fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                        fontWeight={800}
                        textTransform="uppercase"
                        letterSpacing="wider"
                        color={headerColor}
                        mb={3}
                        position="relative"
                    >
                        Background
                        <Box
                            as="span"
                            position="absolute"
                            bottom="-5px"
                            left="50%"
                            transform="translateX(-50%)"
                            width="60%"
                            height="4px"
                            bgGradient="linear(to-r, blue.400, purple.500)"
                            borderRadius="full"
                        />
                    </Text>
                    <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color={useColorModeValue("gray.500", "gray.400")}
                        mt={4}
                        textAlign="center"
                        maxW="600px"
                    >
                        My educational journey and professional experience
                    </Text>
                </Flex>

                {/* Modern Slim Tab Navigation */}
                <Tabs
                    variant="unstyled"
                    isFitted
                    size="md"
                    index={tabIndex}
                    onChange={(index) => setTabIndex(index)}
                >
                    <TabList
                        mb={10}
                        bg={tabBg}
                        p={1.5}
                        borderRadius="full"
                        borderWidth="1px"
                        borderColor={borderColor}
                        boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                        maxW="500px"
                        mx="auto"
                        position="relative"
                    >
                        <Tab
                            _selected={{
                                bg: tabSelectedBg,
                                color: tabSelectedColor,
                                boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                            }}
                            _hover={{
                                color: useColorModeValue("blue.600", "blue.300"),
                            }}
                            color={tabColor}
                            borderRadius="full"
                            py={3}
                            px={6}
                            fontWeight={600}
                            fontSize={{ base: "sm", md: "md" }}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                            position="relative"
                            overflow="hidden"
                        >
                            <Flex align="center" gap={2}>
                                <Box
                                    as={FaGraduationCap}
                                    size={18}
                                    transition="transform 0.3s ease"
                                    _groupHover={{ transform: "rotate(-10deg)" }}
                                />
                                <Text>Education</Text>
                            </Flex>
                        </Tab>
                        <Tab
                            _selected={{
                                bg: tabSelectedBg,
                                color: tabSelectedColor,
                                boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
                            }}
                            _hover={{
                                color: useColorModeValue("blue.600", "blue.300"),
                            }}
                            color={tabColor}
                            borderRadius="full"
                            py={3}
                            px={6}
                            fontWeight={600}
                            fontSize={{ base: "sm", md: "md" }}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                            position="relative"
                            overflow="hidden"
                        >
                            <Flex align="center" gap={2}>
                                <Box
                                    as={FaBriefcase}
                                    size={16}
                                    transition="transform 0.3s ease"
                                    _groupHover={{ transform: "rotate(10deg)" }}
                                />
                                <Text>Experience</Text>
                            </Flex>
                        </Tab>
                    </TabList>

                    <Box position="relative" minH="500px" mt={8}>
                        <AnimatePresence mode="wait" initial={false}>
                            {tabIndex === 0 ? (
                                <motion.div
                                    key="education-tab"
                                    initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                                    animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                                    exit={{ opacity: 0, x: 50, filter: "blur(10px)" }}
                                    transition={{ duration: 0.8, ease: "anticipate" }}
                                    style={{ width: "100%" }}
                                >
                                    <Education />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="experience-tab"
                                    initial={{ opacity: 0, x: 50, filter: "blur(10px)" }}
                                    animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                                    exit={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                                    transition={{ duration: 0.8, ease: "anticipate" }}
                                    style={{ width: "100%" }}
                                >
                                    <Experience />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </Tabs>
            </Box>
        </Box>
    );
};

export default EducationExperience;
